import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { Box, Fab, Tooltip } from '@mui/material';
import AssistantIcon from '@mui/icons-material/Assistant';
import { useTheme } from '@mui/material/styles';
import { sendApiRequest } from 'lib/api/public';
import { useExtensionDetection } from 'lib/useExtensionDetection';
import notify from 'lib/notify';
import {
  getCommonStyles,
  getVersionInfo,
  resolveOnboardingVersionForTaskIds,
} from './constants';
import {
  DOWNLOAD_EXTENSION_TASK_ID,
  useDownloadExtensionAutoComplete,
} from './extensionInstallOnboarding';
import { useLinkedInSourcingModalSync } from './linkedinSourcingOnboarding';
import { getOnboardingTaskTrackRequest, trackOnboardingTask } from './trackOnboardingTask';
import RecentlyViewed from 'components/RecentlyViewed';
import AnchoredTourGuide, {
  countTourTargets,
  dismissTourTargetModals,
  findTourTarget,
  getFormTourHighlightElement,
  scrollTourTargetIntoView,
  TOUR_GUIDE_PANEL_SELECTOR,
  TOUR_HIGHLIGHT_SYNC_MS,
  TOUR_TARGET_POLL_MS,
  toTourHighlightRect,
  type AnchoredTourHighlightRect,
  type AnchoredTourStep,
} from './AnchoredTourGuide';
import OnboardingPopup from './OnboardingPopup';
import {
  fetchCompletedTaskIds,
  fetchOnboardingProgress,
  mergeTasksWithCompletion,
  type HarborOnboardingVersion,
  type OnboardingTask,
} from './onboarding';
import {
  FORM_TOUR_CONFIGS,
  getFormTourPrimaryLabel,
  getFormTourStepAction,
  getFormTourStepAutoAdvanceRules,
  getFormTourStepPauseIndexIfTargetSatisfied,
  getFormTourStepPauseIndexAfterBack,
  isFormTourActiveOnPath,
  isFormTourStepInteractionClick,
  isFormTourStepPausedForAutoAdvance,
  isTourTargetClick,
  resolveFormTourHighlightTestIds,
  runFormTourPrimaryAction,
  shouldDismissFormTourOnNavigate,
  shouldShowFormTourPrimaryButton,
  type FormTourConfig,
} from './formTour';
import {
  ADD_SENDER_ACCOUNT_OAUTH_SUCCESS_KEY,
  clearFormTourRowCountBaseline,
  persistFormTourRowCountBaseline,
  readFormTourRowCountBaseline,
} from './formTourRowCountBaseline';
import { addSenderAccountTour } from './addSenderAccountTour';
import {
  CONNECT_CALENDAR_TASK_ID,
  useConnectCalendarAutoComplete,
} from './connectCalendarTour';
import { clearActiveOnboardingFormTourTaskId, setActiveOnboardingFormTourTaskId } from './onboardingFormTourState';

type ActiveFormTour = {
  taskId: string;
  storageKey: string;
  stepIndex: number;
};

const ONBOARDING_VERSION_STORAGE_KEY = 'onboarding-active-version';

const readPersistedOnboardingVersion = (): HarborOnboardingVersion => {
  if (typeof window === 'undefined') {
    return 'harbor101';
  }

  const storedVersion = sessionStorage.getItem(ONBOARDING_VERSION_STORAGE_KEY);

  if (storedVersion === 'harbor101' || storedVersion === 'harbor102' || storedVersion === 'harbor103') {
    return storedVersion;
  }

  return 'harbor101';
};

const OnboardingRobot = () => {
  const router = useRouter();
  const theme = useTheme();
  const { installed: extensionInstalled } = useExtensionDetection();

  const commonStyles = useMemo(() => getCommonStyles(theme), [theme]);

  const [isOpen, setIsOpen] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<HarborOnboardingVersion>(readPersistedOnboardingVersion);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [userSelectedVersion, setUserSelectedVersion] = useState(false);
  const [activeFormTour, setActiveFormTour] = useState<ActiveFormTour | null>(null);
  const [formTourHighlightRect, setFormTourHighlightRect] = useState<AnchoredTourHighlightRect | null>(null);
  const [formTourContextHighlightRect, setFormTourContextHighlightRect] = useState<AnchoredTourHighlightRect | null>(
    null,
  );
  const [pausedAutoAdvanceStepIndex, setPausedAutoAdvanceStepIndex] = useState<number | null>(null);
  const pausedAutoAdvanceStepIndexRef = useRef<number | null>(null);
  const formTourStepDirectionRef = useRef<'forward' | 'back'>('forward');
  const dismissedOnBackTestIdsRef = useRef<readonly string[]>([]);
  const completingFormTourViaBackendRef = useRef(false);
  const hasRestoredFormTourRef = useRef(false);
  const previousPathnameRef = useRef<string | null>(null);
  const previousActiveFormTourRef = useRef<ActiveFormTour | null>(null);

  const loadOnboardingData = useCallback(
    async (options?: { forceResponseVersion?: boolean }) => {
      const response = await fetchOnboardingProgress();

      if (response?.allCompleted) {
        setHidden(true);
        return;
      }

      if (!response) {
        setTasks([]);
        return;
      }

      const shouldUseResponseVersion = options?.forceResponseVersion || !userSelectedVersion;
      const targetVersion = shouldUseResponseVersion ? response.version : currentVersion;

      if (shouldUseResponseVersion) {
        setCurrentVersion(response.version);
        sessionStorage.setItem(ONBOARDING_VERSION_STORAGE_KEY, response.version);
      }

      setCompletedTasks(new Set(response.tasks));

      const versionInfo = getVersionInfo(targetVersion);

      if (!options?.forceResponseVersion && userSelectedVersion && targetVersion !== response.version) {
        try {
          const completed = await fetchCompletedTaskIds(targetVersion, versionInfo.allTaskIds);
          setTasks(mergeTasksWithCompletion(versionInfo.taskList as OnboardingTask[], completed));
        } catch {
          setTasks(mergeTasksWithCompletion(versionInfo.taskList as OnboardingTask[], response.tasks));
        }
      } else {
        setTasks(mergeTasksWithCompletion(versionInfo.taskList as OnboardingTask[], response.tasks));
      }

      setHidden(false);

      const hasPersistedFormTour =
        typeof window !== 'undefined' &&
        FORM_TOUR_CONFIGS.some(config => sessionStorage.getItem(config.storageKey) !== null);

      if (!hasPersistedFormTour && localStorage.getItem('onboarding-robot-hidden') !== 'true') {
        setIsOpen(true);
      }
    },
    [userSelectedVersion, currentVersion],
  );

  useEffect(() => {
    void loadOnboardingData();

    const interval = setInterval(() => {
      void loadOnboardingData();
    }, 10000);

    return () => clearInterval(interval);
  }, [loadOnboardingData]);

  useEffect(() => {
    if (!router.isReady || hasRestoredFormTourRef.current) {
      return;
    }

    hasRestoredFormTourRef.current = true;

    for (const config of FORM_TOUR_CONFIGS) {
      const persistedStep = sessionStorage.getItem(config.storageKey);

      if (persistedStep === null) {
        continue;
      }

      const parsedStep = Number.parseInt(persistedStep, 10);

      if (Number.isNaN(parsedStep) || parsedStep < 0 || parsedStep >= config.steps.length) {
        sessionStorage.removeItem(config.storageKey);
        continue;
      }

      setCurrentTaskId(config.taskId);
      setDialogOpen(false);
      setIsOpen(false);
      setActiveFormTour({
        taskId: config.taskId,
        storageKey: config.storageKey,
        stepIndex: parsedStep,
      });
      break;
    }
  }, [router.isReady, router.pathname]);

  useEffect(() => {
    if (!activeFormTour) {
      if (previousActiveFormTourRef.current) {
        FORM_TOUR_CONFIGS.forEach(config => {
          sessionStorage.removeItem(config.storageKey);
          clearFormTourRowCountBaseline(config.storageKey);
        });
      }

      previousActiveFormTourRef.current = null;
      return;
    }

    previousActiveFormTourRef.current = activeFormTour;

    FORM_TOUR_CONFIGS.forEach(config => {
      if (config.storageKey === activeFormTour.storageKey) {
        sessionStorage.setItem(config.storageKey, String(activeFormTour.stepIndex));
        return;
      }

      sessionStorage.removeItem(config.storageKey);
    });
  }, [activeFormTour]);

  useEffect(() => {
    setActiveOnboardingFormTourTaskId(activeFormTour?.taskId ?? null);
  }, [activeFormTour?.taskId, activeFormTour]);

  useEffect(() => () => clearActiveOnboardingFormTourTaskId(), []);

  const markTasksCompleted = useCallback(
    async (taskIds: string[], isCompleted = false): Promise<boolean> => {
      const version = resolveOnboardingVersionForTaskIds(taskIds, currentVersion);

      try {
        await sendApiRequest({
          path: '/account/complete-onboarding-task',
          data: { taskIds, isCompleted, version },
        });

        const newCompletedTasks = new Set([...completedTasks, ...taskIds]);
        setCompletedTasks(newCompletedTasks);
        setTasks(prevTasks => prevTasks.map(task => (taskIds.includes(task.id) ? { ...task, completed: true } : task)));

        notify('Completed!');
        setDialogOpen(false);
        return true;
      } catch {
        notify('Failed to update task status');
        setDialogOpen(false);
        return false;
      }
    },
    [completedTasks, currentVersion],
  );

  useDownloadExtensionAutoComplete(tasks, markTasksCompleted);
  useConnectCalendarAutoComplete(tasks, markTasksCompleted);

  const displayTasks = useMemo(() => {
    return tasks.map(task => {
      if (task.id !== DOWNLOAD_EXTENSION_TASK_ID) {
        return task;
      }

      if (extensionInstalled) {
        return task;
      }

      return {
        ...task,
        completed: false,
      };
    });
  }, [tasks, extensionInstalled]);

  useLinkedInSourcingModalSync({
    dialogOpen,
    currentTaskId,
    completedTasks,
    setDialogOpen,
    loadOnboardingData,
  });

  const markAllTasksCompleted = useCallback(async () => {
    const versionInfo = getVersionInfo(currentVersion);
    await markTasksCompleted(versionInfo.allTaskIds, true);
  }, [currentVersion, markTasksCompleted]);

  const handleTaskClick = (taskId: string) => {
    const formTourConfig = FORM_TOUR_CONFIGS.find(config => config.taskId === taskId);

    if (formTourConfig) {
      setCurrentTaskId(taskId);
      setDialogOpen(false);
      setIsOpen(false);
      setActiveFormTour({
        taskId: formTourConfig.taskId,
        storageKey: formTourConfig.storageKey,
        stepIndex: 0,
      });

      const tourConfig = formTourConfig as FormTourConfig;

      if (!isFormTourActiveOnPath(tourConfig, router.pathname, 0) && !(tourConfig.navStepCount ?? 0)) {
        void router.push(tourConfig.startPath);
      }
      return;
    }

    setCurrentTaskId(taskId);
    setDialogOpen(true);
  };

  const switchToVersion = async (version: HarborOnboardingVersion) => {
    const versionInfo = getVersionInfo(version);
    setUserSelectedVersion(true);
    setCurrentVersion(version);
    sessionStorage.setItem(ONBOARDING_VERSION_STORAGE_KEY, version);

    try {
      const completed = await fetchCompletedTaskIds(version, versionInfo.allTaskIds);
      setTasks(mergeTasksWithCompletion(versionInfo.taskList as OnboardingTask[], completed));
      setCompletedTasks(completed);
      setIsOpen(true);
    } catch {
      setTasks(
        (versionInfo.taskList as OnboardingTask[]).map(task => ({
          ...task,
          completed: false,
        })),
      );
      setIsOpen(true);
    }
  };

  const goToNextVersion = () => {
    if (currentVersion === 'harbor103') {
      return;
    }

    const nextVersion: HarborOnboardingVersion = currentVersion === 'harbor101' ? 'harbor102' : 'harbor103';
    void switchToVersion(nextVersion);
  };

  const togglePopup = () => {
    localStorage.setItem('onboarding-robot-hidden', isOpen ? 'true' : 'false');
    setIsOpen(!isOpen);
  };

  const activeFormTourConfig = activeFormTour
    ? (FORM_TOUR_CONFIGS.find(config => config.taskId === activeFormTour.taskId) ?? null)
    : null;

  const isFormTourUiVisible = Boolean(
    activeFormTour &&
    activeFormTourConfig &&
    router.isReady &&
    isFormTourActiveOnPath(activeFormTourConfig, router.pathname, activeFormTour.stepIndex),
  );

  const closeActiveFormTour = useCallback(() => {
    sessionStorage.removeItem(ADD_SENDER_ACCOUNT_OAUTH_SUCCESS_KEY);
    pausedAutoAdvanceStepIndexRef.current = null;
    setPausedAutoAdvanceStepIndex(null);
    setActiveFormTour(null);
    setFormTourHighlightRect(null);
    setFormTourContextHighlightRect(null);
    setCurrentTaskId(null);
  }, []);

  const completeActiveFormTour = useCallback(() => {
    if (!activeFormTour) {
      return;
    }

    const completedTaskId = activeFormTour.taskId;
    clearFormTourRowCountBaseline(activeFormTour.storageKey);
    sessionStorage.removeItem(ADD_SENDER_ACCOUNT_OAUTH_SUCCESS_KEY);
    closeActiveFormTour();
    setIsOpen(true);
    void markTasksCompleted([completedTaskId]);
  }, [activeFormTour, closeActiveFormTour, markTasksCompleted]);

  const completeActiveFormTourViaBackend = useCallback(async () => {
    if (!activeFormTour || completingFormTourViaBackendRef.current) {
      return;
    }

    completingFormTourViaBackendRef.current = true;

    const completedTaskId = activeFormTour.taskId;
    const observerPanel = findTourTarget(['@observer-link-panel']);
    const jobId =
      observerPanel?.getAttribute('data-job-id') ??
      (typeof router.query.jobId === 'string' ? router.query.jobId : null) ??
      (typeof router.query.id === 'string' ? router.query.id : null);
    const candidateId =
      (typeof router.query.candidateId === 'string' ? router.query.candidateId : null) ??
      (typeof router.query.id === 'string' ? router.query.id : null);

    try {
      const trackRequest = getOnboardingTaskTrackRequest(completedTaskId, { jobId, candidateId });

      if (trackRequest) {
        await trackOnboardingTask(trackRequest);
      }

      clearFormTourRowCountBaseline(activeFormTour.storageKey);
      sessionStorage.removeItem(ADD_SENDER_ACCOUNT_OAUTH_SUCCESS_KEY);
      closeActiveFormTour();
      setIsOpen(true);

      const version = resolveOnboardingVersionForTaskIds([completedTaskId], currentVersion);
      const completed = await fetchCompletedTaskIds(version, [completedTaskId]);

      if (completed.has(completedTaskId)) {
        await loadOnboardingData();
        notify('Completed!');
        return;
      }

      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise(resolve => window.setTimeout(resolve, 500));
        const polled = await fetchCompletedTaskIds(version, [completedTaskId]);

        if (polled.has(completedTaskId)) {
          await loadOnboardingData();
          notify('Completed!');
          return;
        }
      }

      await loadOnboardingData();
    } catch {
      notify('Failed to update task status');
    } finally {
      completingFormTourViaBackendRef.current = false;
    }
  }, [
    activeFormTour,
    closeActiveFormTour,
    currentVersion,
    loadOnboardingData,
    router.query.id,
    router.query.jobId,
    router.query.candidateId,
  ]);

  const finishActiveFormTour = useCallback(() => {
    if (!activeFormTourConfig) {
      return;
    }

    if ((activeFormTourConfig as FormTourConfig).completeTaskViaBackend) {
      void completeActiveFormTourViaBackend();
      return;
    }

    completeActiveFormTour();
  }, [activeFormTourConfig, completeActiveFormTour, completeActiveFormTourViaBackend]);

  const advanceFormTourToStep = useCallback(
    (stepIndex: number) => {
      if (!activeFormTourConfig) {
        return;
      }

      formTourStepDirectionRef.current = 'forward';
      const pauseAt = getFormTourStepPauseIndexIfTargetSatisfied(activeFormTourConfig, stepIndex);
      pausedAutoAdvanceStepIndexRef.current = pauseAt;
      setPausedAutoAdvanceStepIndex(pauseAt);
      setActiveFormTour(prev => (prev ? { ...prev, stepIndex } : prev));
    },
    [activeFormTourConfig],
  );

  const getActiveFormTourStep = useCallback((): AnchoredTourStep | null => {
    if (!activeFormTour || !activeFormTourConfig) {
      return null;
    }

    if (!isFormTourActiveOnPath(activeFormTourConfig, router.pathname, activeFormTour.stepIndex)) {
      return null;
    }

    return activeFormTourConfig.steps[activeFormTour.stepIndex] as AnchoredTourStep;
  }, [activeFormTour, activeFormTourConfig, router.pathname]);

  const getActiveFormTourHighlightTarget = useCallback(() => {
    const step = getActiveFormTourStep();

    if (!step || !activeFormTourConfig || activeFormTour === null) {
      return null;
    }

    const highlightTestIds = resolveFormTourHighlightTestIds(step);
    const expandModal = Boolean(step.highlightExpandModal);

    return getFormTourHighlightElement(highlightTestIds, { expandModal });
  }, [activeFormTour, activeFormTourConfig, getActiveFormTourStep, router.pathname]);

  const getActiveFormTourContextTarget = useCallback(() => {
    const step = getActiveFormTourStep();

    if (!step?.contextTargetTestIds?.length) {
      return null;
    }

    return getFormTourHighlightElement(step.contextTargetTestIds, { expandModal: true });
  }, [getActiveFormTourStep]);

  useEffect(() => {
    if (!isFormTourUiVisible) {
      return;
    }

    const target = getActiveFormTourHighlightTarget();

    if (!target) {
      return;
    }

    return scrollTourTargetIntoView(target);
  }, [isFormTourUiVisible, getActiveFormTourHighlightTarget]);

  useEffect(() => {
    if (!isFormTourUiVisible) {
      setFormTourHighlightRect(null);
      setFormTourContextHighlightRect(null);
      return;
    }

    const syncTargetRect = () => {
      const step = getActiveFormTourStep();
      const highlightPaddingTop = step?.highlightPaddingTop;
      const highlightTarget = getActiveFormTourHighlightTarget();
      const contextTarget = getActiveFormTourContextTarget();
      setFormTourHighlightRect(
        highlightTarget
          ? toTourHighlightRect(highlightTarget.getBoundingClientRect(), highlightTarget, {
              paddingTop: highlightPaddingTop,
            })
          : null,
      );
      setFormTourContextHighlightRect(
        contextTarget ? toTourHighlightRect(contextTarget.getBoundingClientRect(), contextTarget) : null,
      );
    };

    syncTargetRect();

    window.addEventListener('resize', syncTargetRect);
    window.addEventListener('scroll', syncTargetRect, true);

    const interval = window.setInterval(syncTargetRect, TOUR_HIGHLIGHT_SYNC_MS);

    return () => {
      window.removeEventListener('resize', syncTargetRect);
      window.removeEventListener('scroll', syncTargetRect, true);
      window.clearInterval(interval);
    };
  }, [isFormTourUiVisible, getActiveFormTourContextTarget, getActiveFormTourHighlightTarget, getActiveFormTourStep]);

  const tourTargetCountBaselineRef = useRef(0);

  useEffect(() => {
    if (!isFormTourUiVisible || !activeFormTour || !activeFormTourConfig) {
      return;
    }

    const rules = getFormTourStepAutoAdvanceRules(activeFormTourConfig, activeFormTour.stepIndex);
    const countRule = rules.find(rule => rule.completeTour && rule.completeWhenCountIncreases);

    if (!countRule) {
      tourTargetCountBaselineRef.current = 0;
      return;
    }

    const storedBaseline = readFormTourRowCountBaseline(activeFormTour.storageKey);

    tourTargetCountBaselineRef.current = storedBaseline ?? countTourTargets(countRule.testIds);
  }, [isFormTourUiVisible, activeFormTour, activeFormTourConfig]);

  useEffect(() => {
    if (!router.isReady || activeFormTour?.taskId !== 'add_sender_account') {
      return;
    }

    if (sessionStorage.getItem(ADD_SENDER_ACCOUNT_OAUTH_SUCCESS_KEY) !== '1') {
      return;
    }

    const tryCompleteAfterOAuth = () => {
      const baseline = readFormTourRowCountBaseline(addSenderAccountTour.storageKey);
      const rowCount = countTourTargets(['@sender-accounts-row']);

      if (baseline !== null ? rowCount > baseline : rowCount > 0) {
        completeActiveFormTour();
      }
    };

    tryCompleteAfterOAuth();

    const interval = window.setInterval(tryCompleteAfterOAuth, TOUR_TARGET_POLL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [router.isReady, activeFormTour?.taskId, completeActiveFormTour]);

  useEffect(() => {
    if (activeFormTour?.taskId !== CONNECT_CALENDAR_TASK_ID) {
      return;
    }

    const connectCalendarTask = tasks.find(task => task.id === CONNECT_CALENDAR_TASK_ID);

    if (!connectCalendarTask?.completed) {
      return;
    }

    closeActiveFormTour();
    setIsOpen(true);
  }, [activeFormTour?.taskId, tasks, closeActiveFormTour]);

  useEffect(() => {
    if (!isFormTourUiVisible || !activeFormTour || !activeFormTourConfig) {
      return;
    }

    const rules = getFormTourStepAutoAdvanceRules(activeFormTourConfig, activeFormTour.stepIndex);

    if (!rules.length) {
      return;
    }

    const tryAdvance = () => {
      if (
        activeFormTour &&
        isFormTourStepPausedForAutoAdvance(
          activeFormTourConfig,
          activeFormTour.stepIndex,
          pausedAutoAdvanceStepIndexRef.current ?? pausedAutoAdvanceStepIndex,
        )
      ) {
        return;
      }

      for (const rule of rules) {
        if (rule.completeTour) {
          if (rule.completeWhenCountIncreases) {
            if (countTourTargets(rule.testIds) > tourTargetCountBaselineRef.current) {
              finishActiveFormTour();
              return;
            }

            continue;
          }

          const isSatisfied = rule.waitUntilAbsent
            ? rule.testIds.every(testId => !findTourTarget([testId]))
            : Boolean(findTourTarget(rule.testIds));

          if (isSatisfied) {
            finishActiveFormTour();
            return;
          }

          continue;
        }

        const shouldAdvance = rule.waitUntilAbsent
          ? rule.testIds.every(testId => !findTourTarget([testId]))
          : Boolean(findTourTarget(rule.testIds));

        if (!shouldAdvance) {
          continue;
        }

        const advanceTo = rule.advanceTo;

        if (advanceTo !== undefined) {
          if (rule.captureRowCountBaselineTestIds?.length) {
            persistFormTourRowCountBaseline(activeFormTour.storageKey, rule.captureRowCountBaselineTestIds);
          }

          advanceFormTourToStep(advanceTo);
          return;
        }
      }
    };

    const interval = window.setInterval(tryAdvance, TOUR_TARGET_POLL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [
    isFormTourUiVisible,
    activeFormTour,
    activeFormTourConfig,
    advanceFormTourToStep,
    finishActiveFormTour,
    pausedAutoAdvanceStepIndex,
  ]);

  useEffect(() => {
    if (!isFormTourUiVisible || !activeFormTour || !activeFormTourConfig) {
      return;
    }

    const pauseAt =
      formTourStepDirectionRef.current === 'back'
        ? getFormTourStepPauseIndexAfterBack(activeFormTourConfig, activeFormTour.stepIndex, {
            ignoreSatisfiedTestIds: dismissedOnBackTestIdsRef.current,
          })
        : getFormTourStepPauseIndexIfTargetSatisfied(activeFormTourConfig, activeFormTour.stepIndex);

    dismissedOnBackTestIdsRef.current = [];
    formTourStepDirectionRef.current = 'forward';
    pausedAutoAdvanceStepIndexRef.current = pauseAt;
    setPausedAutoAdvanceStepIndex(pauseAt);
  }, [isFormTourUiVisible, activeFormTour?.stepIndex, activeFormTourConfig]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const previousPathname = previousPathnameRef.current;

    if (activeFormTour && activeFormTourConfig && previousPathname) {
      if (
        shouldDismissFormTourOnNavigate(
          activeFormTourConfig,
          previousPathname,
          router.pathname,
          activeFormTour.stepIndex,
        )
      ) {
        const action = getFormTourStepAction(activeFormTourConfig, activeFormTour.stepIndex);

        if (action?.type === 'complete') {
          if ((activeFormTourConfig as FormTourConfig).completeTaskViaBackend) {
            closeActiveFormTour();
          } else {
            completeActiveFormTour();
          }
        } else {
          closeActiveFormTour();
        }
      }
    }

    previousPathnameRef.current = router.pathname;
  }, [
    activeFormTour,
    activeFormTourConfig,
    closeActiveFormTour,
    completeActiveFormTour,
    router.isReady,
    router.pathname,
  ]);

  useEffect(() => {
    if (!isFormTourUiVisible || !activeFormTour || !activeFormTourConfig) {
      return;
    }

    const step = activeFormTourConfig.steps[activeFormTour.stepIndex] as AnchoredTourStep;
    const clickTestIds = step.completeWhenTargetClicked;

    if (!clickTestIds?.length) {
      return;
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest(TOUR_GUIDE_PANEL_SELECTOR)) {
        return;
      }

      if (!isTourTargetClick(target, clickTestIds)) {
        return;
      }

      if ((activeFormTourConfig as FormTourConfig).completeTaskViaBackend) {
        void completeActiveFormTourViaBackend();
        return;
      }

      completeActiveFormTour();
    };

    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
    };
  }, [
    isFormTourUiVisible,
    activeFormTour,
    activeFormTourConfig,
    completeActiveFormTour,
    completeActiveFormTourViaBackend,
  ]);

  useEffect(() => {
    if (!isFormTourUiVisible || !activeFormTour || !activeFormTourConfig) {
      return;
    }

    if (pausedAutoAdvanceStepIndex !== activeFormTour.stepIndex) {
      return;
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest(TOUR_GUIDE_PANEL_SELECTOR)) {
        return;
      }

      if (!isFormTourStepInteractionClick(activeFormTourConfig, activeFormTour.stepIndex, target)) {
        return;
      }

      pausedAutoAdvanceStepIndexRef.current = null;
      setPausedAutoAdvanceStepIndex(null);
    };

    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
    };
  }, [isFormTourUiVisible, activeFormTour, activeFormTourConfig, pausedAutoAdvanceStepIndex]);

  const handleActiveFormTourPrimaryAction = () => {
    if (!activeFormTour || !activeFormTourConfig) {
      return;
    }

    runFormTourPrimaryAction(
      {
        config: activeFormTourConfig,
        stepIndex: activeFormTour.stepIndex,
        router,
        goToStep: advanceFormTourToStep,
        onComplete: finishActiveFormTour,
      },
      pausedAutoAdvanceStepIndex,
    );
  };

  const handleActiveFormTourBack = () => {
    if (!activeFormTour || !activeFormTourConfig || activeFormTour.stepIndex <= 0) {
      return;
    }

    const currentStep = activeFormTourConfig.steps[activeFormTour.stepIndex] as AnchoredTourStep;

    if (currentStep.disableBack) {
      return;
    }

    const closedTargetTestIds = currentStep.closeTargetTestIdsOnBack ?? [];
    dismissedOnBackTestIdsRef.current = closedTargetTestIds;
    dismissTourTargetModals(closedTargetTestIds);

    formTourStepDirectionRef.current = 'back';
    const nextStepIndex = activeFormTour.stepIndex - 1;
    const pauseAt = getFormTourStepPauseIndexAfterBack(activeFormTourConfig, nextStepIndex, {
      ignoreSatisfiedTestIds: closedTargetTestIds,
    });
    pausedAutoAdvanceStepIndexRef.current = pauseAt;
    setPausedAutoAdvanceStepIndex(pauseAt);

    setActiveFormTour(prev => (prev ? { ...prev, stepIndex: nextStepIndex } : prev));
  };

  const renderActiveFormTour = () => {
    if (!activeFormTour || !activeFormTourConfig || !router.isReady) {
      return null;
    }

    if (!isFormTourActiveOnPath(activeFormTourConfig, router.pathname, activeFormTour.stepIndex)) {
      return null;
    }

    const highlightRect = formTourHighlightRect;
    const contextHighlightRect = formTourContextHighlightRect;

    const primaryLabel = getFormTourPrimaryLabel(activeFormTourConfig, activeFormTour.stepIndex, router.pathname);
    const showPrimaryButton = shouldShowFormTourPrimaryButton(
      activeFormTourConfig,
      activeFormTour.stepIndex,
      pausedAutoAdvanceStepIndex,
    );

    return (
      <AnchoredTourGuide
        activeStepIndex={activeFormTour.stepIndex}
        steps={activeFormTourConfig.steps}
        highlightRect={highlightRect}
        contextHighlightRect={contextHighlightRect}
        overlineLabel={activeFormTourConfig.overlineLabel}
        primaryLabel={primaryLabel}
        showPrimaryButton={showPrimaryButton}
        onClose={closeActiveFormTour}
        onBack={handleActiveFormTourBack}
        onPrimaryAction={handleActiveFormTourPrimaryAction}
      />
    );
  };

  const currentTask = currentTaskId ? tasks.find(task => task.id === currentTaskId) : null;

  if (hidden || router.query.accountId) {
    return <RecentlyViewed />;
  }

  const versionInfo = getVersionInfo(currentVersion);
  const title = versionInfo.title;
  const incompleteTasksCount = displayTasks.filter(t => !t.completed).length;

  const renderModal = () => {
    if (!dialogOpen || !currentTask || !currentTaskId || !currentTask.renderComponent) {
      return null;
    }

    return currentTask.renderComponent({
      onClose: () => setDialogOpen(false),
      onSuccess: () => {
        setDialogOpen(false);
        void markTasksCompleted([currentTaskId]);
      },
      markAllTasksCompleted: () => void markAllTasksCompleted(),
      markAsCompleted: () => void markTasksCompleted([currentTaskId]),
    });
  };

  const handleBackToPreviousPart = () => {
    const previousVersion: HarborOnboardingVersion = currentVersion === 'harbor103' ? 'harbor102' : 'harbor101';
    void switchToVersion(previousVersion);
  };

  return (
    <>
      {renderModal()}
      {renderActiveFormTour()}

      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: '16px', sm: '20px' },
          right: { xs: '16px', sm: '20px' },
          zIndex: 1050,
        }}
      >
        <Tooltip title={title} placement="left">
          <Fab color="secondary" aria-label="onboarding-assistant" onClick={togglePopup} sx={commonStyles.fabButton}>
            <AssistantIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
          </Fab>
        </Tooltip>

        {incompleteTasksCount > 0 && (
          <Box sx={commonStyles.badgeStyle} role="status" aria-label={`${incompleteTasksCount} tasks remaining`}>
            {incompleteTasksCount}
          </Box>
        )}
      </Box>

      {isOpen && (
        <OnboardingPopup
          currentVersion={currentVersion}
          tasks={displayTasks}
          onCloseClick={togglePopup}
          onTaskClick={handleTaskClick}
          onBackToPreviousPart={handleBackToPreviousPart}
          onGoToNextPart={goToNextVersion}
        />
      )}
    </>
  );
};

export default OnboardingRobot;
